//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { Api, LocalDictionaryFactory } from './../api/api.module'
import { LoggerService } from './../common/common.module'
import { MeterModel, FieldNameValueModel, ColumnModel } from '../api/api.models'
import { EventsNames } from '../../helpers/events-names'
import { BaseGridGridsterController, ScopeBaseGridsterGrid } from '../common/common.base.grid.gridster.controller'
import { GridOptions, GridTypes, GridController, ButtonsOptions } from '../common/grid/grid.controller'
import { Enums } from '../../models/common/enums'
import { CommonHelper } from '../../helpers/common-helper'
import { ConnectedObject } from '../common/connected-object/connected-object.directive'
import { GridDetailsController } from '../common/grid-details/grid-details.directive.controller'
import { UserSessionService } from '../../services/user-session'

import { MapOptions, MapLegendItem } from '../common/map2/map.directive.controller'

import { LocalizationHelper } from '../../helpers/localization-helper'
import { MapHelper } from '../../helpers/map-helper'

import { OpBounds } from '../../models/custom/op-map-area'
import { Activity } from '../../models/common'
import { TemplateHelper, ConfigHelper, LogHelper } from '../../helpers'
import { MeterService } from '../../services/meter'
import { GridContextMenuService } from '../../services/grid'
import { OpMeter } from '../../models/core'
import { CreateEditResult, DetailsResult, SystemService } from '../../services/system'
import { ImportOptions } from '../common/import'
import { ExportOptions } from '../common/export'
import { IAlarmDefinitionsBulkEditOptions, IAlarmDefinitionsEdit } from '../common/alarm-definition/alarm-definitions-edit'
import { GridCustomButtonsOptions, GridCustomButtonLocation } from '../common/grid'

export interface DefinitionsBridge {
    id?: number
    distributorId?: number
    items: any[]
    groups?: any[]
    onSubmit?: (defs: any[], removed: any[], fields?: FieldNameValueModel[]) => ng.IPromise<boolean>
    popupTitle: string
    meter: MeterModel
    connectedDevice: ConnectedObject
}

export interface MetersScope
    extends ScopeBaseGridsterGrid {
    mainGridOptions: GridOptions
    alarmDefinitionsEditSource: {
        alarmDefinitionsOptions: IAlarmDefinitionsBulkEditOptions,
        onInitialized: (component: IAlarmDefinitionsEdit) => void
    }

    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }
}

export class MetersController
    extends BaseGridGridsterController {

    //#region Consts

    private get METERS_GRID_NAME(): string { return 'MetersGrid' }

    //#endregion

    //#region Members

    private grid: GridController<OpMeter>
    private alarmDefinitionsEditController: IAlarmDefinitionsEdit
    private alarmDefinitionBulkEditGridButton: DevExpress.ui.dxButton

    //#endregion

    //#region Ctor

    constructor(
        protected $scope: MetersScope,
        protected $state: StateService,
        protected $timeout: ng.ITimeoutService,
        protected api: Api,
        private logger: LoggerService,
        protected localDictionary: LocalDictionaryFactory,
        protected userSession: UserSessionService,
        protected meterService: MeterService,
        private gridContextMenuService: GridContextMenuService,
        protected systemService: SystemService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super(
            $scope,
            $timeout,
            $state,
            userSession,
            systemService,
            api,
            localDictionary,
            null,
            GridDetailsController.getOptionsByReferenceType(referenceType),
            Enums.SimaxModuleName[moduleName],
            referenceType,
            true
        )

        this.setConfirmationsOptions()
        this.setEvents()
        this.setMainGridOptions()
        this.setAlarmDefinitionsEditSource()
    }

    //#endregion

    //#region Events

    private onInitialized(e: any, grid: GridController<OpMeter>): void {
        this.dataGrid = e.component
        this.grid = grid

        this.setGridId(e.tableId)
        this.onGridInit(e.tableId)
    }

    private onAdd(template: number): void {
        this.meterService
            .openCreate(template)
            .then((result: CreateEditResult<OpMeter>) => {
                if (result.status === CreateEditResult.Status.Success) {
                    this.onSaved();
                }
            });
    }

    private onConfirmDelete(): void {
        this.meterService
            .deleteMeter(this.grid.selectedItemId)
            .then(() => {
                this.onDeleted()
            })
            .fail((error: any) => {
                LogHelper.error(MeterService.LOG_PREFIX, error)
            })
    }

    private onEdit(): void {
        if (this.grid.selectedCount === 1) {
            const idMeterTypeClass: number = this.grid.selectedItem.meterType != null ? this.grid.selectedItem.meterType.idMeterTypeClass : null
            const idMeter = this.grid.selectedItemId

            this.meterService
                .openEdit(idMeter, idMeterTypeClass)
                .then((result: CreateEditResult<OpMeter>) => {
                    if (result.status === CreateEditResult.Status.Success) {
                        this.onSaved();
                    }
                });
        } else {
            const columnCount: number = this.grid.dxGrid.columnCount()
            const columns: any = []

            for (let i = 0; i < columnCount; ++i) {
                columns.push(this.grid.dxGrid.columnOption(i));
            }

            this.meterService
                .openBulkEdit(columns, this.grid.selectedGridItems)
                .then((result: CreateEditResult<OpMeter>) => {
                    if (result.status === CreateEditResult.Status.Success || result.status === CreateEditResult.Status.Error) {
                        this.onSaved();
                    }
                });
        }
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onDeleted(): void {
        this.grid.refresh()
    }

    private onSaved(): void {
        this.grid.refresh();
    }

    private onShowDetails(e: any): void {
        this.meterService
            .openDetails(this.grid.selectedItemId, this.grid.pageItemsIds)
            .then((result: DetailsResult) => {
                if (result.status === DetailsResult.Status.Deleted || result.status === DetailsResult.Status.Edited) {
                    this.grid.refresh();
                }
            });
    }

    private showAlarmDefsMultiple(): void {
        if (this.meterService.isMetersSameClass(this.grid.selectedItems)) {
            this.$scope.alarmDefinitionsEditSource.alarmDefinitionsOptions.gridDataSource = this.grid.dxGrid.getSelectedRowsData() || []
            this.alarmDefinitionsEditController.showBulkEdit()
        }
    }

    private toggleAlarmDefinitionBulkEditButton(): void {
        if (this.alarmDefinitionBulkEditGridButton != null) {
            this.alarmDefinitionBulkEditGridButton.option('disabled', !this.areCopyTariffCompletable())
        }
    }

    private areCopyTariffCompletable(): boolean {
        return _.size(this.grid.selectedItems) > 0
    }

    //#endregion Events End

    //#region Setup

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,

            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.METER_LIST) ? "['" + Activity[Activity.METER_LIST] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.METER_DEL) ? "['" + Activity[Activity.METER_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.METER_EDIT) ? "['" + Activity[Activity.METER_EDIT] + "']" : null,
            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.METER_ADD) ? "['" + Activity[Activity.METER_ADD] + "']" : null,
            importPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.METER_IMPORT_OP) ? "['" + Activity[Activity.METER_IMPORT_OP] + "']" : null, // TODO: W przyszłości jak przejdziemy na nowe kontrolki do importu/exportu będzie trzeba użyć tych uprawnień, żeby nie zmieniać teraz nazwy
            exportPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.METER_EXPORT_OP) ? "['" + Activity[Activity.METER_EXPORT_OP] + "']" : null, // TODO: W przyszłości jak przejdziemy na nowe kontrolki do importu/exportu będzie trzeba użyć tych uprawnień, żeby nie zmieniać teraz nazwy
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,

            onShowClick: (e: any) => this.onShowDetails(e),
            onAddClick: (template: number) => this.onAdd(template),
            onEditClick: () => this.onEdit(),
            onDeleteClick: () => this.onDelete(),
            onShowMap: () => this.showMapDetails(),
            onShowPreview: () => this.showPreviewDetails(),
        }
    }

    private getGridCustomButtonOptions(): GridCustomButtonsOptions {
        const buttons: GridCustomButtonsOptions = { buttons: [] }

        if (this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DEF_MULTIPLE_ADD)) {
            buttons.buttons.push({
                location: GridCustomButtonLocation.Dropdown,
                options: {
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.ALARMS, this.userSession),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefinitionBulkEdit),

                    onClick: (e: any) => {
                        this.showAlarmDefsMultiple()
                    },

                    onInitialized: (e: any) => {
                        this.alarmDefinitionBulkEditGridButton = e.component
                        this.toggleAlarmDefinitionBulkEditButton()
                    }
                }
            })
        }
        return buttons
    }

    private getImportOptions(): ImportOptions {
        return {
            popupName: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ImportMeters),
            referenceType: this.referenceType,
            moduleName: this.moduleName,
            columnsReorderingDisabled: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.MeterModuleMainGrid),
            dictionaryMethodName: null,
            requestMethodType: null
        }
    }

    private getExportOptions(): ExportOptions {
        return {
            referenceType: this.referenceType,
            pluginOptions: {
                moduleName: this.moduleName,
                popupName: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EXPORT, this.moduleName)
            }
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onDeleted()
        )

        this.$scope.showDetails = (e: any) => this.onShowDetails(e)
    }

    private setMainGridOptions(): void {
        this.meterService
            .getMainGridColumns()
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    customButtonOptions: this.getGridCustomButtonOptions(),
                    columnsDictionary: columns,
                    dataSourceParser: this.meterService.getMainGridDataSourceParser(),
                    defaultColumnsDictionary: defaultColumns,
                    exportOptions: this.getExportOptions(),
                    getDataSourceMethod: this.userSession.getDataProvider().Meter.getMeterFilter,
                    importOptions: this.getImportOptions(),
                    idReferenceType: this.referenceType,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.meterSaved,
                    name: this.METERS_GRID_NAME,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    referenceId: this.referenceType,
                    type: GridTypes.MAIN,

                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.MeterModuleMainGrid),

                    contextMenuItems: (isTouchScreen: boolean = false) => this.getContextMenuItems(isTouchScreen),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onInitialized: (e: any, grid: GridController<OpMeter>) => this.onInitialized(e, grid),
                    onRowDoubleClick: (e: any) => this.onShowDetails(e),
                    onSelectionChanged: (e: any) => this.onSelectionChanged(e),
                    onUpdateGridsterPosition: (e: any) => this.setGridsterPosition(e)
                }
            })
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items = this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdMeter, {
            idReferenceTypes: this.grid.selectedItemsIds,
            onEdit: () => this.onEdit(),
            getCorrespondingActionType: (idReferenceType: Enums.ReferenceType, args: any) => this.getCorrespondingActionType(idReferenceType, args),
        })

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    private setAlarmDefinitionsEditSource(): void {
        this.$scope.alarmDefinitionsEditSource = {
            alarmDefinitionsOptions: {
                moduleName: this.moduleName,
                referenceType: this.referenceType,
                referenceObject: null,
                onAlarmConfigurableSaved: () => { },
                sendConfigurationIdActivity: Activity.LOCATION_CONFIGURATION_SEND,
                getEentityRequestMethod: (idMeter: number) => this.meterService.getMeter(idMeter),
                getColumnsMethod: () => this.meterService.getMainGridColumns(),
                gridDataSource: []
            },
            onInitialized: (component: IAlarmDefinitionsEdit) => {
                this.alarmDefinitionsEditController = component
            }
        }
    }


    // BaseGridGridsterController Implementation

    protected onGridsterMapConfiguration(mapOptions: MapOptions): void {
        mapOptions.enableAreaSelect = true

        mapOptions.onAreaSelect = (bounds: OpBounds) => {
            MapHelper
                .getCoordColumnNames(this.userSession.getDataProvider(), Enums.ReferenceType.IdMeter)
                .then(([latColName, lonColName]: [string, string]) => {
                    this.addCustomGridFilter(CommonHelper.getLonLatFilter(bounds, latColName, lonColName, 'MetersFilterByLocations'))
                })
        }

        MapHelper
            .getLegendItems(this.userSession.getDataProvider(), Enums.ReferenceType.IdMeter)
            .then((items: MapLegendItem[]) => {
                mapOptions.legendItems = items
            })
    }

    // BaseGridGridsterController Implementation End
}

